import { create } from 'zustand';
import TokenOptionsApi from '../utils/TokenOptionsApi';

// Define interfaces for our data structures
interface Category {
  id: string;
  value: string;
  label: string;
  count: number | null;
}

interface RawTokenData {
  market_cap: number;
  id: string;
  symbol?: string;
  name?: string;
  current_price?: number;
  fully_diluted_valuation?: number;
  price_change_percentage_1h_in_currency?: number;
  price_change_percentage_24h_in_currency?: number;
  image?: string;
}

export interface TokenData {
  marketCap: number;
  id: string;
  symbol: string;
  name: string;
  price: number;
  fdv: number;
  change1h: number;
  change24h: number;
  image: string;
}

interface SearchResult {
  coins: Array<{ id: string }>;
  exchanges: unknown[];
  icos: unknown[];
  categories: Category[];
  nfts: unknown[];
}

interface TokenOptionsState {
  cryptos: TokenData[];
  categories: Category[];
  lastUpdated: number | null;
  updateInterval: number;
  maxUSDC: number;
  page: number;
  loading: boolean;
  activeCategory: string;
  inputSearch: string;
  fetchData: () => Promise<void>;
  loadMore: () => Promise<void>;
  searchToken: () => Promise<void>;
  getMaxUSDC: () => Promise<number>;
  setActiveCategory: (value: string) => void;
  setInputSearch: (value: string) => void;
  resetPage: () => void;
  utils: {
    formatNumber: (number: number, decimals?: number) => string;
    formatMarketData: (data: TokenData[]) => TokenData[];
  };
}

// Initial categories
const initialCategories: Category[] = [
  { id: 'all', value: '', label: 'All Crypto', count: null },
  { id: 'memes', value: 'meme-token', label: 'Memes', count: null },
  { id: 'sol', value: 'solana-ecosystem', label: 'SOL', count: null },
  { id: 'depin', value: 'depin', label: 'DePIN', count: null },
  { id: 'ai', value: 'artificial-intelligence', label: 'AI', count: null },
];

// Helper function to format price and market data
const formatMarketData = (data: TokenData[]): TokenData[] => {
  return data.map(crypto => {
    return {
      ...crypto,
      price: parseFloat(crypto.price?.toFixed(2) || '0'),
      change1h: parseFloat(crypto.change1h?.toFixed(2) || '0'),
      change24h: parseFloat(crypto.change24h?.toFixed(2) || '0'),
      marketCap: Math.floor(crypto.marketCap || 0),
    };
  });
};

const generateMarketData = (data: RawTokenData[]): TokenData[] => {
  const result: TokenData[] = [];

  for (let i = 0; i < data.length; i++) {
    result.push({
      id: data[i].id,
      symbol: data[i]?.symbol?.toUpperCase() || '',
      name: data[i]?.name || '',
      price: data[i]?.current_price || 0,
      fdv: data[i]?.fully_diluted_valuation || 0,
      change1h: data[i]?.price_change_percentage_1h_in_currency || 0,
      change24h: data[i]?.price_change_percentage_24h_in_currency || 0,
      image: data[i]?.image || '',
      marketCap: data[i]?.market_cap || 0,
    });
  }

  return result;
};

// Helper function to format numbers
export const formatNumber = (number: number, decimals = 2): string => {
  if (number >= 1e9) {
    return `$${(number / 1e9).toFixed(decimals)}B`;
  }
  if (number >= 1e6) {
    return `$${(number / 1e6).toFixed(decimals)}M`;
  }
  if (number >= 1e3) {
    return `$${(number / 1e3).toFixed(decimals)}K`;
  }
  return `$${number.toFixed(decimals)}`;
};

// helper function for search
export function matchesCategory(searchData: SearchResult, categoryId: string): boolean {
  if (!categoryId) return true;
  return searchData.categories.some(category => category.id === categoryId);
}

const useTokenOptionsStore = create<TokenOptionsState>((set, get) => ({
  // State
  cryptos: [],
  categories: [],
  lastUpdated: null,
  updateInterval: 60000, // 1 minute
  maxUSDC: 23000, // Mock max USDC available
  page: 1,
  loading: false,
  activeCategory: '',
  inputSearch: '',

  // Actions
  // to fetch data and refresh loaded pages
  fetchData: async () => {
    const { page, activeCategory } = get();
    set({
      loading: true,
    });
    try {
      const data = await TokenOptionsApi.getTokenMarketData({
        perPage: page * 10,
        category: activeCategory,
      });
      // Format and transform the data
      const formattedData = formatMarketData(generateMarketData(data));

      set({
        cryptos: formattedData,
        categories: initialCategories,
        lastUpdated: Date.now(),
        loading: false,
      });
    } catch (error) {
      console.log(error);
      set({
        cryptos: [],
        loading: false,
      });
    }
  },

  loadMore: async () => {
    const { page, cryptos, activeCategory } = get();
    set({
      loading: true,
    });
    const nextPage = page + 1;
    try {
      const data = await TokenOptionsApi.getTokenMarketData({
        page: nextPage,
        category: activeCategory,
      });

      // Format and transform the data
      const formattedData = formatMarketData(generateMarketData(data));

      set({
        page: nextPage,
        cryptos: [...cryptos, ...formattedData],
        categories: initialCategories,
        lastUpdated: Date.now(),
        loading: false,
      });
    } catch (error) {
      console.log(error);
      set({
        cryptos: [],
        loading: false,
      });
    }
  },

  searchToken: async () => {
    const { activeCategory, inputSearch, fetchData } = get();
    let idsList = [];
    let searchData: SearchResult = {
      coins: [],
      exchanges: [],
      icos: [],
      categories: [],
      nfts: [],
    };

    set({
      loading: true,
    });
    try {
      if (inputSearch) {
        searchData = await TokenOptionsApi.getTokenSearchData({
          query: inputSearch || '',
        });
      } else {
        fetchData();
        return;
      }
      // Process the search results
      if (searchData.coins.length === 0) {
        throw new Error('No search results found');
      }

      const isMatchesCategory = activeCategory
        ? searchData.categories.some(category => category.id === activeCategory)
        : true;

      if (isMatchesCategory) {
        idsList = searchData.coins.map(coin => coin.id);
      } else {
        throw new Error('No category matches');
      }

      const data = await TokenOptionsApi.getTokenMarketData({
        ids: idsList as never[],
        perPage: 250,
      });

      // Format and transform the data
      const formattedData = formatMarketData(generateMarketData(data));

      set({
        cryptos: formattedData,
        lastUpdated: Date.now(),
        loading: false,
      });
    } catch (error) {
      console.log(error);
      set({
        cryptos: [],
        loading: false,
      });
    }
  },

  getMaxUSDC: async () => {
    return get().maxUSDC;
  },

  setActiveCategory: value => {
    set({
      activeCategory: value,
    });
  },

  setInputSearch: value => {
    set({
      inputSearch: value,
    });
  },

  resetPage: () => {
    set({
      page: 1,
    });
  },

  // Utils exported for convenience
  utils: {
    formatNumber,
    formatMarketData,
  },
}));

export default useTokenOptionsStore;
